import { useCallback, useEffect, useState, type FormEvent, type ReactNode } from 'react';
import { Link, useParams } from 'react-router-dom';
import {
  AlertTriangle,
  BadgeCheck,
  BookOpen,
  CalendarClock,
  Paperclip,
  ClipboardList,
  FileText,
  Mail,
  Plus,
  Power,
  Trash2,
  TriangleAlert,
} from 'lucide-react';
import {
  ACTION_PLAN_ORIGIN_LABEL,
  ACTION_PLAN_STATUS_LABEL,
  AUDIT_KIND_LABEL,
  AUDIT_STATUS_LABEL,
  CERT_STANDARD_LABEL,
  CERT_STATUS_LABEL,
  ENTITY_STATUS_LABEL,
  HOMOLOGATION_STATUS_LABEL,
  SUPPLIER_DOC_KIND_LABEL,
  SUPPLIER_KIND_LABEL,
  UserRole,
  type PendencyDto,
  type SupplierProfileDto,
  type ManualAcceptanceDto,
} from '@weloze/shared';
import { api, ApiError } from '../../lib/api';
import { FilePicker } from '../../components/FilePicker';
import { useConfirm } from '../../components/ConfirmDialog';
import { useAuth } from '../../lib/auth';
import { usePageLabel } from '../../lib/navigation';
import {
  formatCnpj,
  formatCompetence,
  formatDate,
  formatDateTime,
  formatPlace,
} from '../../lib/format';
import {
  Alert,
  Badge,
  Button,
  buttonVariants,
  Card,
  EmptyState,
  Field,
  Modal,
  Select,
  Skeleton,
  Textarea,
} from '../../components/ui';

/**
 * `/admin/fornecedores/:storeId` — a ficha que substitui o `RQ 115-05`.
 *
 * O painel do topo é o motivo da tela existir: em vez de alguém manter colunas
 * de controle na planilha, o portal **deriva** as pendências dos documentos
 * cadastrados. Renovar a LO faz o alerta sumir sozinho — ninguém precisa
 * lembrar de apagar nada.
 *
 * Não há campo de nota em lugar nenhum aqui, de propósito: o ISQ vem do
 * certificado, e a planilha antiga já divergia de si mesma por deixar uma
 * pessoa digitar esse número.
 */
export function SupplierProfilePage() {
  const { storeId = '' } = useParams();
  const { user } = useAuth();
  const isAdmin = user?.role === UserRole.WELOZE_ADMIN;

  const [profile, setProfile] = useState<SupplierProfileDto | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      setProfile(await api.get<SupplierProfileDto>(`/stores/${storeId}/profile`));
    } catch (err) {
      setError(
        err instanceof ApiError && err.status === 404
          ? 'Fornecedor não encontrado.'
          : 'Não foi possível carregar a ficha.',
      );
    } finally {
      setLoading(false);
    }
  }, [storeId]);

  useEffect(() => {
    void load();
  }, [load]);

  // Publica o nome na trilha: sem isso o breadcrumb mostraria o UUID da URL.
  usePageLabel(profile ? `${profile.storeName} · ${profile.protheusRef}` : null);

  if (loading) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-10 w-96" />
        <Skeleton className="h-24" />
        <div className="grid gap-4 lg:grid-cols-2">
          <Skeleton className="h-64" />
          <Skeleton className="h-64" />
        </div>
      </div>
    );
  }

  if (error || !profile) return <Alert>{error ?? 'Ficha indisponível.'}</Alert>;

  return (
    <div className="space-y-6">
      <Header profile={profile} isAdmin={isAdmin} onChanged={load} />
      <PendencyPanel pendencies={profile.pendencies} />

      <div className="grid gap-4 lg:grid-cols-2">
        <IdentityCard profile={profile} isAdmin={isAdmin} onChanged={load} />
        <NonconformitiesCard profile={profile} />
        <CertificationsCard profile={profile} />
        <ContactsCard profile={profile} isAdmin={isAdmin} onChanged={load} />
        <DocumentsCard profile={profile} isAdmin={isAdmin} onChanged={load} />
        <AuditsCard profile={profile} isAdmin={isAdmin} onChanged={load} />
        <ActionPlansCard profile={profile} isAdmin={isAdmin} onChanged={load} />
        <ManualAcceptanceCard storeId={profile.storeId} />
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------

function Header({
  profile,
  isAdmin,
  onChanged,
}: {
  profile: SupplierProfileDto;
  isAdmin: boolean;
  onChanged: () => void;
}) {
  const [confirming, setConfirming] = useState(false);
  const [working, setWorking] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function disable() {
    setWorking(true);
    setError(null);
    try {
      await api.delete(`/monitoring/${profile.storeId}`);
      setConfirming(false);
      onChanged();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Falha ao desligar o monitoramento.');
    } finally {
      setWorking(false);
    }
  }

  /**
   * A competência mais recente que já pode ter relatório: o **mês passado**.
   *
   * O mês corrente ainda não fechou, e apontar para ele levaria a uma tela que
   * diz "não publicada" — pior que não ter o botão.
   */
  const hoje = new Date();
  const anterior = new Date(Date.UTC(hoje.getUTCFullYear(), hoje.getUTCMonth() - 1, 1));
  const ultimaCompetencia = {
    year: anterior.getUTCFullYear(),
    month: anterior.getUTCMonth() + 1,
  };

  return (
    <header className="flex flex-wrap items-start justify-between gap-4">
      <div className="min-w-0">
        <div className="flex flex-wrap items-center gap-2">
          <h1 className="text-2xl font-semibold tracking-tight text-slate-900">
            {profile.storeName}
          </h1>
          <span className="rounded bg-slate-100 px-2 py-0.5 font-mono text-sm text-slate-700">
            {profile.protheusRef}
          </span>
          {profile.monitored ? (
            <Badge tone="success">
              monitorado desde {formatCompetence(profile.monitoredFrom)}
            </Badge>
          ) : (
            <Badge>
              {profile.monitoredUntil
                ? `monitorado até ${formatCompetence(profile.monitoredUntil)}`
                : 'não monitorado'}
            </Badge>
          )}
          {profile.isPreferred && <Badge tone="brand">preferencial</Badge>}
          {profile.status !== 'ACTIVE' && (
            <Badge tone="danger">{ENTITY_STATUS_LABEL[profile.status]}</Badge>
          )}
        </div>
        <p className="mt-1 text-sm text-slate-600">
          {profile.legalName} · {formatCnpj(profile.cnpj)} ·{' '}
          {formatPlace(profile.city, profile.state)}
        </p>
      </div>

      <div className="flex flex-wrap gap-2">
        {/* A ficha respondia tudo sobre cadastro e nada sobre desempenho, que é
            o motivo de o portal existir. O relatório fica a um clique. */}
        {profile.monitored && (
          <Link
            to={`/relatorio/${profile.storeId}/${ultimaCompetencia.year}/${ultimaCompetencia.month}`}
            className={buttonVariants({ variant: 'outline' })}
          >
            <FileText className="size-4" aria-hidden />
            Relatório da competência
          </Link>
        )}
        {isAdmin && profile.monitored && (
          <Button variant="outline" onClick={() => setConfirming(true)}>
            <Power className="size-4" aria-hidden />
            Desligar monitoramento
          </Button>
        )}
      </div>

      <Modal
        open={confirming}
        onClose={() => setConfirming(false)}
        title="Desligar o monitoramento?"
        description="A loja deixa de entrar nos cálculos das próximas competências. A ficha, as avaliações e o histórico continuam no portal — nada é apagado."
      >
        <Alert variant="info">
          Os usuários desta loja <strong>perdem o acesso ao portal imediatamente</strong>. Religar o
          monitoramento não devolve o acesso: ele precisa ser concedido de novo, um a um.
        </Alert>
        {error && <Alert>{error}</Alert>}
        <div className="mt-4 flex justify-end gap-2">
          <Button variant="ghost" onClick={() => setConfirming(false)}>
            Cancelar
          </Button>
          <Button variant="primary" loading={working} onClick={() => void disable()}>
            Desligar
          </Button>
        </div>
      </Modal>
    </header>
  );
}

/**
 * Painel de pendências — o topo da ficha.
 *
 * Tudo aqui é derivado dos documentos. Não existe botão de "resolver": some
 * quando o documento é cadastrado ou renovado.
 */
function PendencyPanel({ pendencies }: { pendencies: PendencyDto[] }) {
  if (pendencies.length === 0) {
    return (
      <Card className="border-emerald-200 bg-emerald-50/50">
        <p className="text-sm font-medium text-emerald-800">Ficha em dia.</p>
        <p className="mt-1 text-sm text-emerald-700">
          Nenhum documento vencido, faltando ou a vencer nos próximos 60 dias.
        </p>
      </Card>
    );
  }

  const critical = pendencies.filter((p) => p.severity === 'CRITICAL').length;

  return (
    <Card className={critical > 0 ? 'border-red-200 bg-red-50/40' : 'border-amber-200 bg-amber-50/40'}>
      <div className="flex items-center gap-2">
        <TriangleAlert
          className={critical > 0 ? 'size-4 text-red-600' : 'size-4 text-amber-600'}
          aria-hidden
        />
        <h2 className="font-medium text-slate-900">
          {pendencies.length} pendência{pendencies.length === 1 ? '' : 's'} na ficha
        </h2>
      </div>
      <ul className="mt-3 space-y-2">
        {pendencies.map((pendency, index) => (
          <li key={`${pendency.kind}-${index}`} className="flex items-start gap-2 text-sm">
            <Badge tone={pendency.severity === 'CRITICAL' ? 'danger' : 'warning'}>
              {pendency.severity === 'CRITICAL' ? 'crítico' : 'aviso'}
            </Badge>
            <span className="text-slate-700">{pendency.label}</span>
          </li>
        ))}
      </ul>
    </Card>
  );
}

function Section({
  title,
  icon,
  action,
  children,
}: {
  title: string;
  icon: ReactNode;
  action?: ReactNode;
  children: ReactNode;
}) {
  return (
    <Card>
      <div className="mb-4 flex items-center justify-between gap-3">
        <h2 className="flex items-center gap-2 font-medium text-slate-900">
          <span className="text-slate-400">{icon}</span>
          {title}
        </h2>
        {action}
      </div>
      {children}
    </Card>
  );
}

/**
 * As RNCs desta loja, e o caminho para o 8D.
 *
 * A ficha é onde se olha **um** fornecedor, e quem a abre para entender uma nota
 * baixa precisa ver o que a derrubou. Sem esta lista, a ficha respondia tudo
 * sobre documentos e nada sobre o que de fato move o IQA.
 *
 * As abertas primeiro, porque são as que ainda pedem alguma coisa.
 */
function NonconformitiesCard({ profile }: { profile: SupplierProfileDto }) {
  const rncs = profile.nonconformities;
  const abertas = rncs.filter((rnc) => rnc.status !== 'CLOSED' && rnc.status !== 'CANCELLED');
  const ordenadas = [...abertas, ...rncs.filter((rnc) => !abertas.includes(rnc))];

  return (
    <Section
      title="RNCs"
      icon={<TriangleAlert className="size-4" aria-hidden />}
      action={
        <div className="flex items-center gap-3 text-sm">
          {/*
            Emitir daqui evita reencontrar, numa lista de 44, o fornecedor que já
            está na tela. O formulário é o mesmo — a ficha só manda junto a
            resposta que ela já sabe.
          */}
          <Link
            to={`/rncs?novo=${profile.storeId}`}
            className="text-brand-700 underline-offset-4 hover:underline"
          >
            Emitir RNC
          </Link>
          <Link to="/rncs" className="text-brand-700 underline-offset-4 hover:underline">
            Ver todas
          </Link>
        </div>
      }
    >
      {rncs.length === 0 ? (
        <p className="text-sm text-slate-600">Nenhuma RNC emitida para esta loja.</p>
      ) : (
        <>
          <p className="mb-2 text-sm text-slate-600">
            {abertas.length === 0
              ? `${rncs.length} RNC${rncs.length === 1 ? '' : 's'} no histórico, nenhuma em tratativa.`
              : `${abertas.length} em tratativa, de ${rncs.length} no histórico.`}
          </p>
          <ul className="divide-y divide-slate-100">
            {ordenadas.slice(0, 8).map((rnc) => {
              const atrasadas = rnc.stages.filter((etapa) => etapa.overdue);
              return (
                <li key={rnc.id} className="flex flex-wrap items-center gap-x-2 gap-y-1 py-2 text-sm">
                  <Link
                    to={`/rncs/${rnc.id}`}
                    className="rounded bg-slate-100 px-1.5 py-0.5 font-mono text-xs font-medium text-slate-800 underline-offset-4 hover:bg-slate-200 hover:underline"
                  >
                    {rnc.number}
                  </Link>
                  <span className="min-w-0 flex-1 truncate text-slate-700">{rnc.description}</span>
                  <span className="text-xs text-slate-500">{formatDate(rnc.issuedAt)}</span>
                  {atrasadas.length > 0 && (
                    <Badge tone="danger">
                      {atrasadas.length === 1
                        ? `${atrasadas[0]!.label} vencida`
                        : `${atrasadas.length} etapas vencidas`}
                    </Badge>
                  )}
                  {rnc.status === 'CLOSED' && <Badge tone="success">encerrada</Badge>}
                </li>
              );
            })}
          </ul>
          {ordenadas.length > 8 && (
            <p className="mt-2 text-xs text-slate-500">
              e mais {ordenadas.length - 8} no histórico.
            </p>
          )}
        </>
      )}
    </Section>
  );
}

/**
 * Os certificados do SGQ desta loja.
 *
 * Até 08/2026 eles só existiam na lista global, e quem abria a ficha para
 * entender uma nota baixa não via o certificado que a derrubou. Não se confundem
 * com os documentos logo abaixo: aqueles são de ficha (LO, RQ 093, CQI) e não
 * entram em índice nenhum; estes são o que move o ISQ.
 *
 * A ficha **mostra e não edita**: cadastrar, aprovar e recusar continuam na tela
 * de Certificações, que é onde a fila de validação é trabalhada. Dois lugares
 * para o mesmo cadastro é como as duas telas passam a discordar.
 */
function CertificationsCard({ profile }: { profile: SupplierProfileDto }) {
  const certificados = profile.certifications;

  return (
    <Section
      title="Certificações"
      icon={<BadgeCheck className="size-4" aria-hidden />}
      action={
        <Link
          to="/admin/certificacoes"
          className="text-sm text-brand-700 underline-offset-4 hover:underline"
        >
          Gerenciar
        </Link>
      }
    >
      {certificados.length === 0 ? (
        <p className="text-sm text-slate-600">
          Nenhum certificado cadastrado. Sem certificação vigente, o ISQ desta loja fica em 0 — é um
          degrau legítimo da régua, mas o pior deles.
        </p>
      ) : (
        <ul className="divide-y divide-slate-100">
          {certificados.map((cert) => {
            const vencido = cert.daysUntilExpiry < 0;
            const vencendo = !vencido && cert.daysUntilExpiry <= 60;
            return (
              <li key={cert.id} className="flex flex-wrap items-center gap-x-2 gap-y-1 py-2 text-sm">
                <span className="font-medium text-slate-800">
                  {CERT_STANDARD_LABEL[cert.standard]}
                </span>
                {cert.number && (
                  <span className="font-mono text-xs text-slate-500">{cert.number}</span>
                )}
                {cert.isIatfRoadmap && <Badge tone="accent">cronograma IATF</Badge>}
                <span className="ml-auto text-slate-600">
                  {vencido ? 'venceu em ' : 'até '}
                  {formatDate(cert.validUntil)}
                </span>
                {cert.reviewStatus === 'PENDING' && <Badge tone="warning">aguardando validação</Badge>}
                {cert.reviewStatus === 'REJECTED' && <Badge tone="danger">recusado</Badge>}
                {cert.reviewStatus === 'APPROVED' && vencido && <Badge tone="danger">vencido</Badge>}
                {cert.reviewStatus === 'APPROVED' && vencendo && (
                  <Badge tone="warning">vence em {cert.daysUntilExpiry} d</Badge>
                )}
                {cert.status !== 'VALID' && (
                  <Badge tone="danger">{CERT_STATUS_LABEL[cert.status]}</Badge>
                )}
              </li>
            );
          })}
        </ul>
      )}
    </Section>
  );
}

function Row({ label, value }: { label: string; value: ReactNode }) {
  return (
    <div className="flex justify-between gap-4 py-1.5 text-sm">
      <dt className="shrink-0 text-slate-500">{label}</dt>
      <dd className="min-w-0 text-right text-slate-800">{value}</dd>
    </div>
  );
}

// ---------------------------------------------------------------------------

function IdentityCard({
  profile,
  isAdmin,
  onChanged,
}: {
  profile: SupplierProfileDto;
  isAdmin: boolean;
  onChanged: () => void;
}) {
  const [editing, setEditing] = useState(false);

  return (
    <Section
      title="Dados cadastrais"
      icon={<ClipboardList className="size-4" aria-hidden />}
      action={
        isAdmin && (
          <Button variant="ghost" size="sm" onClick={() => setEditing(true)}>
            Editar
          </Button>
        )
      }
    >
      <dl className="divide-y divide-slate-100">
        <Row
          label="Nome de exibição"
          value={
            profile.displayName ?? (
              <span className="text-slate-500">
                {profile.legalName}{' '}
                <span className="text-xs">(a razão social, do ERP)</span>
              </span>
            )
          }
        />
        {/* Do ERP, e por isso sem botão de editar: quem a escreve é a
            sincronização do SA2, e um campo aqui só serviria para as duas
            divergirem. */}
        <Row label="Razão social" value={profile.legalName} />
        <Row label="Nome fantasia" value={profile.companyTradeName ?? '—'} />
        <Row label="CNPJ" value={formatCnpj(profile.cnpj)} />
        <Row label="Cidade/UF" value={formatPlace(profile.city, profile.state)} />
        <Row label="Tipo" value={SUPPLIER_KIND_LABEL[profile.supplierKind]} />
        <Row
          label="Produto/serviço"
          value={profile.productCategories.join(', ') || '—'}
        />
        <Row
          label="Processos especiais"
          value={
            profile.specialProcesses.length > 0 ? (
              <span className="flex flex-wrap justify-end gap-1">
                {profile.specialProcesses.map((process) => (
                  <Badge key={process} tone="accent">
                    {process}
                  </Badge>
                ))}
              </span>
            ) : (
              '—'
            )
          }
        />
        <Row
          label="Característica de segurança"
          value={profile.safetyCharacteristic ? 'Sim' : 'Não'}
        />
        <Row label="Avaliação do ano vigente" value={profile.yearlyReviewMode ?? '—'} />
        <Row label="Homologação" value={HOMOLOGATION_STATUS_LABEL[profile.homologation]} />
        <Row label="Situação" value={ENTITY_STATUS_LABEL[profile.status]} />
        {profile.notes && <Row label="Observações" value={profile.notes} />}
      </dl>

      <EditIdentityModal
        open={editing}
        profile={profile}
        onClose={() => setEditing(false)}
        onDone={() => {
          setEditing(false);
          onChanged();
        }}
      />
    </Section>
  );
}

function EditIdentityModal({
  open,
  profile,
  onClose,
  onDone,
}: {
  open: boolean;
  profile: SupplierProfileDto;
  onClose: () => void;
  onDone: () => void;
}) {
  const [form, setForm] = useState({
    displayName: profile.displayName ?? '',
    tradeName: profile.companyTradeName ?? '',
    cnpj: profile.cnpj ?? '',
    city: profile.city ?? '',
    state: profile.state ?? '',
    supplierKind: profile.supplierKind,
    status: profile.status,
    homologation: profile.homologation,
    productCategories: profile.productCategories.join(', '),
    specialProcesses: profile.specialProcesses.join(', '),
    safetyCharacteristic: profile.safetyCharacteristic,
    yearlyReviewMode: profile.yearlyReviewMode ?? '',
    notes: profile.notes ?? '',
  });
  const [fields, setFields] = useState<Record<string, string>>({});
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  async function submit(event: FormEvent) {
    event.preventDefault();
    setSaving(true);
    setError(null);
    setFields({});
    try {
      await api.put(`/stores/${profile.storeId}`, {
        displayName: form.displayName,
        tradeName: form.tradeName || null,
        cnpj: form.cnpj || null,
        city: form.city || null,
        state: form.state || null,
        supplierKind: form.supplierKind,
        status: form.status,
        homologation: form.homologation,
        productCategories: splitList(form.productCategories),
        specialProcesses: splitList(form.specialProcesses),
        safetyCharacteristic: form.safetyCharacteristic,
        yearlyReviewMode: form.yearlyReviewMode || null,
        notes: form.notes || null,
      });
      onDone();
    } catch (err) {
      if (err instanceof ApiError) {
        setError(err.fields ? null : err.message);
        setFields(err.fields ?? {});
      } else {
        setError('Não foi possível salvar.');
      }
    } finally {
      setSaving(false);
    }
  }

  return (
    <Modal open={open} onClose={onClose} size="lg" title="Editar dados cadastrais">
      <form onSubmit={submit} className="space-y-4">
        <Field
          label="Nome de exibição"
          name="displayName"
          value={form.displayName}
          onChange={(e) => setForm({ ...form, displayName: e.target.value })}
          error={fields.displayName}
          placeholder={profile.legalName}
          hint={
            'É o nome que aparece em toda tela, no ranking e no relatório. ' +
            'Deixe em branco para usar a razão social do ERP.'
          }
        />
        <Field
          label="Nome fantasia"
          name="tradeName"
          value={form.tradeName}
          onChange={(e) => setForm({ ...form, tradeName: e.target.value })}
        />
        <div className="grid gap-3 sm:grid-cols-3">
          <Field
            label="CNPJ"
            name="cnpj"
            className="sm:col-span-1"
            value={form.cnpj}
            onChange={(e) => setForm({ ...form, cnpj: e.target.value })}
            error={fields.cnpj}
          />
          <Field
            label="Cidade"
            name="city"
            value={form.city}
            onChange={(e) => setForm({ ...form, city: e.target.value })}
          />
          <Field
            label="UF"
            name="state"
            maxLength={2}
            value={form.state}
            onChange={(e) => setForm({ ...form, state: e.target.value })}
          />
        </div>
        <div className="grid gap-3 sm:grid-cols-3">
          <Select
            label="Tipo"
            name="supplierKind"
            value={form.supplierKind}
            onChange={(e) =>
              setForm({ ...form, supplierKind: e.target.value as typeof form.supplierKind })
            }
          >
            {Object.entries(SUPPLIER_KIND_LABEL).map(([value, label]) => (
              <option key={value} value={value}>
                {label}
              </option>
            ))}
          </Select>
          <Select
            label="Situação"
            name="status"
            value={form.status}
            onChange={(e) => setForm({ ...form, status: e.target.value as typeof form.status })}
          >
            {Object.entries(ENTITY_STATUS_LABEL).map(([value, label]) => (
              <option key={value} value={value}>
                {label}
              </option>
            ))}
          </Select>
          <Select
            label="Homologação"
            name="homologation"
            value={form.homologation}
            onChange={(e) =>
              setForm({ ...form, homologation: e.target.value as typeof form.homologation })
            }
          >
            {Object.entries(HOMOLOGATION_STATUS_LABEL).map(([value, label]) => (
              <option key={value} value={value}>
                {label}
              </option>
            ))}
          </Select>
        </div>
        <Field
          label="Produto/serviço"
          name="productCategories"
          value={form.productCategories}
          onChange={(e) => setForm({ ...form, productCategories: e.target.value })}
          hint="Separe por vírgula: Aço, Inox"
        />
        <Field
          label="Processos especiais"
          name="specialProcesses"
          value={form.specialProcesses}
          onChange={(e) => setForm({ ...form, specialProcesses: e.target.value })}
          error={fields.specialProcesses}
          hint="CQI-09, CQI-11 ou CQI-12, separados por vírgula."
        />
        <Field
          label="Processo de avaliação do ano vigente"
          name="yearlyReviewMode"
          value={form.yearlyReviewMode}
          onChange={(e) => setForm({ ...form, yearlyReviewMode: e.target.value })}
          hint='"Auto-Avaliação", "Auditoria" ou "NA".'
        />
        <label className="flex items-center gap-2 text-sm text-slate-700">
          <input
            type="checkbox"
            className="size-4 rounded border-slate-300 text-brand-600"
            checked={form.safetyCharacteristic}
            onChange={(e) => setForm({ ...form, safetyCharacteristic: e.target.checked })}
          />
          Fornece item com característica de segurança
        </label>
        <Textarea
          label="Observações"
          name="notes"
          rows={3}
          value={form.notes}
          onChange={(e) => setForm({ ...form, notes: e.target.value })}
        />

        {error && <Alert>{error}</Alert>}
        <div className="flex justify-end gap-2">
          <Button type="button" variant="ghost" onClick={onClose}>
            Cancelar
          </Button>
          <Button type="submit" loading={saving}>
            Salvar
          </Button>
        </div>
      </form>
    </Modal>
  );
}

function splitList(value: string): string[] {
  return value
    .split(',')
    .map((item) => item.trim())
    .filter((item) => item.length > 0);
}

// ---------------------------------------------------------------------------

function ContactsCard({
  profile,
  isAdmin,
  onChanged,
}: {
  profile: SupplierProfileDto;
  isAdmin: boolean;
  onChanged: () => void;
}) {
  const confirm = useConfirm();
  const [adding, setAdding] = useState(false);
  const [form, setForm] = useState({ name: '', email: '', role: '' });
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  async function submit(event: FormEvent) {
    event.preventDefault();
    setSaving(true);
    setError(null);
    try {
      await api.post(`/stores/${profile.storeId}/contacts`, {
        name: form.name,
        email: form.email,
        role: form.role || null,
      });
      setForm({ name: '', email: '', role: '' });
      setAdding(false);
      onChanged();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Não foi possível salvar o contato.');
    } finally {
      setSaving(false);
    }
  }

  return (
    <Section
      title="Contatos"
      icon={<Mail className="size-4" aria-hidden />}
      action={
        isAdmin && (
          <Button variant="ghost" size="sm" onClick={() => setAdding(true)}>
            <Plus className="size-4" aria-hidden />
            Adicionar
          </Button>
        )
      }
    >
      {profile.contacts.length === 0 ? (
        <EmptyState
          title="Nenhum contato cadastrado"
          description="Sem destinatário, o relatório mensal e o aviso de RNC não têm para onde ir."
        />
      ) : (
        <ul className="divide-y divide-slate-100">
          {profile.contacts.map((contact) => (
            <li key={contact.id} className="flex items-center justify-between gap-3 py-2.5">
              <div className="min-w-0">
                <p className="truncate text-sm font-medium text-slate-800">{contact.name}</p>
                <p className="truncate text-xs text-slate-500">
                  {contact.email}
                  {contact.role && ` · ${contact.role}`}
                </p>
              </div>
              {isAdmin && (
                <Button
                  variant="ghost"
                  size="sm"
                  title="Remover contato"
                  onClick={() =>
                    confirm.ask({
                      title: 'Remover contato',
                      body: (
                        <>
                          <p>
                            <strong>{contact.name}</strong> ({contact.email}) deixa de receber os
                            avisos do portal — RNC emitida, competência publicada, documento a
                            vencer.
                          </p>
                          <p>Dá para cadastrar de novo, mas o histórico de envios não volta.</p>
                        </>
                      ),
                      confirmLabel: 'Remover contato',
                      onConfirm: async () => {
                        await api.delete(`/store-contacts/${contact.id}`);
                        onChanged();
                      },
                    })
                  }
                >
                  <Trash2 className="size-4 text-slate-400" aria-hidden />
                  <span className="sr-only">Remover {contact.name}</span>
                </Button>
              )}
            </li>
          ))}
        </ul>
      )}

      {confirm.dialog}

      <Modal open={adding} onClose={() => setAdding(false)} title="Adicionar contato">
        <form onSubmit={submit} className="space-y-3">
          <Field
            label="Nome"
            name="contactName"
            value={form.name}
            onChange={(e) => setForm({ ...form, name: e.target.value })}
          />
          <Field
            label="E-mail"
            name="contactEmail"
            type="email"
            value={form.email}
            onChange={(e) => setForm({ ...form, email: e.target.value })}
          />
          <Field
            label="Função"
            name="contactRole"
            value={form.role}
            onChange={(e) => setForm({ ...form, role: e.target.value })}
            hint="Opcional: Qualidade, Comercial…"
          />
          {error && <Alert>{error}</Alert>}
          <div className="flex justify-end gap-2">
            <Button type="button" variant="ghost" onClick={() => setAdding(false)}>
              Cancelar
            </Button>
            <Button type="submit" loading={saving} disabled={!form.name || !form.email}>
              Adicionar
            </Button>
          </div>
        </form>
      </Modal>
    </Section>
  );
}

// ---------------------------------------------------------------------------

function DocumentsCard({
  profile,
  isAdmin,
  onChanged,
}: {
  profile: SupplierProfileDto;
  isAdmin: boolean;
  onChanged: () => void;
}) {
  const confirm = useConfirm();
  const [adding, setAdding] = useState(false);
  const [form, setForm] = useState({
    kind: 'OPERATING_LICENSE',
    reference: '',
    cycleYear: '',
    issuedAt: '',
    validUntil: '',
  });
  const [fileId, setFileId] = useState<string | null>(null);
  const [fields, setFields] = useState<Record<string, string>>({});
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  async function submit(event: FormEvent) {
    event.preventDefault();
    setSaving(true);
    setError(null);
    setFields({});
    try {
      await api.post(`/stores/${profile.storeId}/documents`, {
        kind: form.kind,
        reference: form.reference || null,
        cycleYear: form.cycleYear ? Number(form.cycleYear) : null,
        issuedAt: form.issuedAt || null,
        validUntil: form.validUntil || null,
        fileId,
      });
      setAdding(false);
      setForm({ kind: 'OPERATING_LICENSE', reference: '', cycleYear: '', issuedAt: '', validUntil: '' });
      setFileId(null);
      onChanged();
    } catch (err) {
      if (err instanceof ApiError) {
        setError(err.fields ? null : err.message);
        setFields(err.fields ?? {});
      } else {
        setError('Não foi possível salvar o documento.');
      }
    } finally {
      setSaving(false);
    }
  }

  return (
    <Section
      title="Documentos da ficha"
      icon={<FileText className="size-4" aria-hidden />}
      action={
        isAdmin && (
          <Button variant="ghost" size="sm" onClick={() => setAdding(true)}>
            <Plus className="size-4" aria-hidden />
            Adicionar
          </Button>
        )
      }
    >
      <p className="mb-3 text-xs text-slate-500">
        LO, auto-avaliação, CQI e termo de aceite. Nenhum deles entra em índice — o certificado do
        SGQ é cadastrado à parte e é o único que afeta o ISQ.
      </p>

      {profile.documents.length === 0 ? (
        <EmptyState title="Nenhum documento cadastrado" />
      ) : (
        <ul className="divide-y divide-slate-100">
          {profile.documents.map((document) => (
            <li key={document.id} className="flex items-center justify-between gap-3 py-2.5">
              <div className="min-w-0">
                <p className="truncate text-sm font-medium text-slate-800">
                  {SUPPLIER_DOC_KIND_LABEL[document.kind]}
                  {document.reference && ` · ${document.reference}`}
                </p>
                <p className="text-xs text-slate-500">
                  {document.issuedAt && `emitido em ${formatDate(document.issuedAt)}`}
                  {document.issuedAt && document.validUntil && ' · '}
                  {document.validUntil && `vence em ${formatDate(document.validUntil)}`}
                  {!document.issuedAt && !document.validUntil && 'sem data registrada'}
                </p>
                {/* Pela rota autenticada, que confere a loja dona e registra
                    quem baixou — nunca link direto para o disco. */}
                {document.fileId && (
                  <a
                    href={`/api/files/${document.fileId}`}
                    className="mt-0.5 inline-flex items-center gap-1 text-xs text-brand-700 underline-offset-4 hover:underline"
                  >
                    <Paperclip className="size-3" aria-hidden />
                    baixar o arquivo
                  </a>
                )}
              </div>
              {isAdmin && (
                <Button
                  variant="ghost"
                  size="sm"
                  title="Remover documento"
                  onClick={() =>
                    confirm.ask({
                      title: 'Apagar documento da ficha',
                      body: (
                        <>
                          <p>
                            <strong>{SUPPLIER_DOC_KIND_LABEL[document.kind]}</strong>
                            {document.reference ? ` · ${document.reference}` : ''} sai da ficha.
                          </p>
                          <p>
                            {document.fileId
                              ? 'O arquivo anexado continua no acervo, mas deixa de ser alcançável por esta tela.'
                              : 'Nenhum arquivo está anexado a este registro.'}
                          </p>
                          <p>
                            As pendências são recalculadas na hora: apagar a LO faz o alerta de
                            "não cadastrada" voltar.
                          </p>
                        </>
                      ),
                      confirmLabel: 'Apagar documento',
                      onConfirm: async () => {
                        await api.delete(`/supplier-documents/${document.id}`);
                        onChanged();
                      },
                    })
                  }
                >
                  <Trash2 className="size-4 text-slate-400" aria-hidden />
                  <span className="sr-only">Remover documento</span>
                </Button>
              )}
            </li>
          ))}
        </ul>
      )}

      {confirm.dialog}

      <Modal open={adding} onClose={() => setAdding(false)} title="Adicionar documento">
        <form onSubmit={submit} className="space-y-3">
          <Select
            label="Tipo"
            name="kind"
            value={form.kind}
            onChange={(e) => setForm({ ...form, kind: e.target.value })}
          >
            {Object.entries(SUPPLIER_DOC_KIND_LABEL).map(([value, label]) => (
              <option key={value} value={value}>
                {label}
              </option>
            ))}
          </Select>
          <div className="grid gap-3 sm:grid-cols-2">
            <Field
              label="Referência"
              name="reference"
              value={form.reference}
              onChange={(e) => setForm({ ...form, reference: e.target.value })}
              error={fields.reference}
              hint="CQI-11, PL2026…"
            />
            <Field
              label="Ciclo"
              name="cycleYear"
              type="number"
              value={form.cycleYear}
              onChange={(e) => setForm({ ...form, cycleYear: e.target.value })}
              hint="Ano, quando o documento for anual."
            />
          </div>
          <div className="grid gap-3 sm:grid-cols-2">
            <Field
              label="Emissão"
              name="issuedAt"
              type="date"
              value={form.issuedAt}
              onChange={(e) => setForm({ ...form, issuedAt: e.target.value })}
            />
            <Field
              label="Vencimento"
              name="validUntil"
              type="date"
              value={form.validUntil}
              onChange={(e) => setForm({ ...form, validUntil: e.target.value })}
              error={fields.validUntil}
            />
          </div>
          <FilePicker
            storeId={profile.storeId}
            value={fileId}
            onChange={(id) => setFileId(id)}
          />
          {error && <Alert>{error}</Alert>}
          <div className="flex justify-end gap-2">
            <Button type="button" variant="ghost" onClick={() => setAdding(false)}>
              Cancelar
            </Button>
            <Button type="submit" loading={saving}>
              Salvar
            </Button>
          </div>
        </form>
      </Modal>
    </Section>
  );
}

// ---------------------------------------------------------------------------

function AuditsCard({
  profile,
  isAdmin,
  onChanged,
}: {
  profile: SupplierProfileDto;
  isAdmin: boolean;
  onChanged: () => void;
}) {
  const [adding, setAdding] = useState(false);
  const [form, setForm] = useState({ kind: 'SECOND_PARTY', plannedFor: '', performedAt: '', status: 'PLANNED' });
  const [fileId, setFileId] = useState<string | null>(null);
  const [fields, setFields] = useState<Record<string, string>>({});
  const [saving, setSaving] = useState(false);

  async function submit(event: FormEvent) {
    event.preventDefault();
    setSaving(true);
    setFields({});
    try {
      await api.post(`/stores/${profile.storeId}/audits`, {
        kind: form.kind,
        plannedFor: form.plannedFor || null,
        performedAt: form.performedAt || null,
        status: form.status,
        fileId,
      });
      setAdding(false);
      setFileId(null);
      onChanged();
    } catch (err) {
      if (err instanceof ApiError) setFields(err.fields ?? {});
    } finally {
      setSaving(false);
    }
  }

  return (
    <Section
      title="Auditorias"
      icon={<CalendarClock className="size-4" aria-hidden />}
      action={
        isAdmin && (
          <Button variant="ghost" size="sm" onClick={() => setAdding(true)}>
            <Plus className="size-4" aria-hidden />
            Adicionar
          </Button>
        )
      }
    >
      {profile.audits.length === 0 ? (
        <EmptyState title="Nenhuma auditoria registrada" />
      ) : (
        <ul className="divide-y divide-slate-100">
          {profile.audits.map((audit) => (
            <li key={audit.id} className="py-2.5">
              <div className="flex items-center justify-between gap-3">
                <p className="text-sm font-medium text-slate-800">{AUDIT_KIND_LABEL[audit.kind]}</p>
                <Badge tone={audit.status === 'DONE' ? 'success' : 'neutral'}>
                  {AUDIT_STATUS_LABEL[audit.status]}
                </Badge>
              </div>
              <p className="text-xs text-slate-500">
                {audit.plannedFor && `prevista para ${formatDate(audit.plannedFor)}`}
                {audit.plannedFor && audit.performedAt && ' · '}
                {audit.performedAt && `realizada em ${formatDate(audit.performedAt)}`}
              </p>
              {audit.fileId && (
                <a
                  href={`/api/files/${audit.fileId}`}
                  className="mt-0.5 inline-flex items-center gap-1 text-xs text-brand-700 underline-offset-4 hover:underline"
                >
                  <Paperclip className="size-3" aria-hidden />
                  baixar o relatório
                </a>
              )}
            </li>
          ))}
        </ul>
      )}

      <Modal open={adding} onClose={() => setAdding(false)} title="Registrar auditoria">
        <form onSubmit={submit} className="space-y-3">
          <Select
            label="Tipo"
            name="auditKind"
            value={form.kind}
            onChange={(e) => setForm({ ...form, kind: e.target.value })}
          >
            {Object.entries(AUDIT_KIND_LABEL).map(([value, label]) => (
              <option key={value} value={value}>
                {label}
              </option>
            ))}
          </Select>
          <Select
            label="Situação"
            name="auditStatus"
            value={form.status}
            onChange={(e) => setForm({ ...form, status: e.target.value })}
          >
            {Object.entries(AUDIT_STATUS_LABEL).map(([value, label]) => (
              <option key={value} value={value}>
                {label}
              </option>
            ))}
          </Select>
          <div className="grid gap-3 sm:grid-cols-2">
            <Field
              label="Prevista para"
              name="plannedFor"
              type="date"
              value={form.plannedFor}
              onChange={(e) => setForm({ ...form, plannedFor: e.target.value })}
            />
            <Field
              label="Realizada em"
              name="performedAt"
              type="date"
              value={form.performedAt}
              onChange={(e) => setForm({ ...form, performedAt: e.target.value })}
              error={fields.performedAt}
            />
          </div>
          <FilePicker
            storeId={profile.storeId}
            value={fileId}
            onChange={(id) => setFileId(id)}
          />
          <div className="flex justify-end gap-2">
            <Button type="button" variant="ghost" onClick={() => setAdding(false)}>
              Cancelar
            </Button>
            <Button type="submit" loading={saving}>
              Salvar
            </Button>
          </div>
        </form>
      </Modal>
    </Section>
  );
}

// ---------------------------------------------------------------------------

function ActionPlansCard({
  profile,
  isAdmin,
  onChanged,
}: {
  profile: SupplierProfileDto;
  isAdmin: boolean;
  onChanged: () => void;
}) {
  const [adding, setAdding] = useState(false);
  const [form, setForm] = useState({ origin: 'OTHER', reference: '', description: '', dueAt: '' });
  const [fileId, setFileId] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  async function submit(event: FormEvent) {
    event.preventDefault();
    setSaving(true);
    try {
      await api.post(`/stores/${profile.storeId}/action-plans`, {
        origin: form.origin,
        reference: form.reference || null,
        description: form.description,
        dueAt: form.dueAt || null,
        fileId,
      });
      setAdding(false);
      setForm({ origin: 'OTHER', reference: '', description: '', dueAt: '' });
      setFileId(null);
      onChanged();
    } finally {
      setSaving(false);
    }
  }

  return (
    <Section
      title="Planos de ação"
      icon={<AlertTriangle className="size-4" aria-hidden />}
      action={
        isAdmin && (
          <Button variant="ghost" size="sm" onClick={() => setAdding(true)}>
            <Plus className="size-4" aria-hidden />
            Solicitar
          </Button>
        )
      }
    >
      {profile.actionPlans.length === 0 ? (
        <EmptyState title="Nenhum plano de ação solicitado" />
      ) : (
        <ul className="divide-y divide-slate-100">
          {profile.actionPlans.map((plan) => (
            <li key={plan.id} className="py-2.5">
              <div className="flex items-start justify-between gap-3">
                <p className="text-sm text-slate-800">{plan.description}</p>
                <Badge
                  tone={
                    plan.status === 'DONE'
                      ? 'success'
                      : plan.status === 'OVERDUE'
                        ? 'danger'
                        : 'neutral'
                  }
                >
                  {ACTION_PLAN_STATUS_LABEL[plan.status]}
                </Badge>
              </div>
              <p className="mt-0.5 flex flex-wrap items-center gap-x-1 text-xs text-slate-500">
                <span>{ACTION_PLAN_ORIGIN_LABEL[plan.origin]}</span>
                {/* Vínculo de verdade, e não o número escrito no `reference`:
                    aqui dá para abrir a RNC que originou o plano. */}
                {plan.nonconformityId && (
                  <>
                    <span>·</span>
                    <Link
                      to={`/rncs/${plan.nonconformityId}`}
                      className="font-mono text-brand-700 underline-offset-4 hover:underline"
                    >
                      RNC {plan.nonconformityNumber ?? ''}
                    </Link>
                  </>
                )}
                {!plan.nonconformityId && plan.reference && <span>· {plan.reference}</span>}
                {plan.dueAt && <span>· prazo {formatDate(plan.dueAt)}</span>}
                {plan.fileId && (
                  <a
                    href={`/api/files/${plan.fileId}`}
                    className="inline-flex items-center gap-1 text-brand-700 underline-offset-4 hover:underline"
                  >
                    <Paperclip className="size-3" aria-hidden />
                    anexo
                  </a>
                )}
              </p>
            </li>
          ))}
        </ul>
      )}

      <Modal open={adding} onClose={() => setAdding(false)} title="Solicitar plano de ação">
        <form onSubmit={submit} className="space-y-3">
          <Select
            label="Origem"
            name="origin"
            value={form.origin}
            onChange={(e) => setForm({ ...form, origin: e.target.value })}
          >
            {Object.entries(ACTION_PLAN_ORIGIN_LABEL).map(([value, label]) => (
              <option key={value} value={value}>
                {label}
              </option>
            ))}
          </Select>
          <Field
            label="Referência"
            name="planReference"
            value={form.reference}
            onChange={(e) => setForm({ ...form, reference: e.target.value })}
            hint="Número da RNC ou o semestre (2026-S1)."
          />
          <Textarea
            label="O que está sendo solicitado"
            name="description"
            rows={4}
            value={form.description}
            onChange={(e) => setForm({ ...form, description: e.target.value })}
          />
          <Field
            label="Prazo"
            name="dueAt"
            type="date"
            value={form.dueAt}
            onChange={(e) => setForm({ ...form, dueAt: e.target.value })}
          />
          <FilePicker
            storeId={profile.storeId}
            value={fileId}
            onChange={(id) => setFileId(id)}
          />
          <div className="flex justify-end gap-2">
            <Button type="button" variant="ghost" onClick={() => setAdding(false)}>
              Cancelar
            </Button>
            <Button type="submit" loading={saving} disabled={!form.description.trim()}>
              Solicitar
            </Button>
          </div>
        </form>
      </Modal>
    </Section>
  );
}

// ---------------------------------------------------------------------------

/**
 * O histórico de aceites do Manual — o que um auditor pede para ver.
 *
 * Mostra o **hash do arquivo aceito**, e não só a edição. Provar quem e quando é
 * fácil; o que costuma faltar numa auditoria é provar **qual texto** — e é isso
 * que o `sha256` responde, inclusive se o documento tiver sido despublicado do
 * portal depois.
 *
 * Carrega sob demanda, e não junto da ficha: é a seção que menos gente abre, e
 * o `GET /stores/:id/profile` já traz sete listas.
 */
function ManualAcceptanceCard({ storeId }: { storeId: string }) {
  const [itens, setItens] = useState<ManualAcceptanceDto[] | null>(null);
  const [erro, setErro] = useState<string | null>(null);

  useEffect(() => {
    let ativo = true;
    api
      .get<ManualAcceptanceDto[]>(`/stores/${storeId}/manual-acceptances`)
      .then((dados) => {
        if (ativo) setItens(dados);
      })
      .catch((err: unknown) => {
        if (ativo) setErro(err instanceof ApiError ? err.message : 'Não foi possível carregar.');
      });
    return () => {
      ativo = false;
    };
  }, [storeId]);

  return (
    <Section title="Aceite do Manual" icon={<BookOpen className="size-4" aria-hidden />}>
      {erro && <Alert>{erro}</Alert>}

      {itens === null ? (
        <Skeleton className="h-12" />
      ) : itens.length === 0 ? (
        <p className="text-sm text-slate-600">
          Nenhum aceite eletrônico registrado. Enquanto a edição vigente não for aceita pelo
          responsável, os usuários desta empresa ficam bloqueados no portal.
        </p>
      ) : (
        <ul className="divide-y divide-slate-100">
          {itens.map((aceite) => (
            <li key={aceite.id} className="py-2 text-sm">
              <div className="flex flex-wrap items-baseline justify-between gap-2">
                <span className="font-medium text-slate-800">
                  {aceite.documentReference ?? aceite.documentTitle}
                </span>
                <span className="text-slate-600">{formatDateTime(aceite.acceptedAt)}</span>
              </div>
              <p className="text-xs text-slate-500">
                {aceite.responsibleName} · {aceite.responsibleRole} · {aceite.userEmail}
                {aceite.ip && ` · IP ${aceite.ip}`}
              </p>
              {/* O hash inteiro, e não um prefixo: ele existe para ser conferido
                  contra o arquivo, e meio hash não confere nada. */}
              <p className="mt-0.5 break-all font-mono text-[11px] text-slate-400">
                sha256 {aceite.documentSha256}
              </p>
            </li>
          ))}
        </ul>
      )}
    </Section>
  );
}
